import { useEffect } from 'react';

/**
 * Whether a keystroke belongs to whatever the user is typing into.
 *
 * Without this check, pressing `q` inside a task title would open Quick Add
 * instead of typing a letter — the single most common way a keyboard shortcut
 * ruins a text field.
 */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable
  );
}

export interface Hotkey {
  /** `event.key`, compared case-insensitively. */
  key: string;
  meta?: boolean;
  shift?: boolean;
  /** Fire even while a text field has focus (Escape, ⌘K). */
  allowInInput?: boolean;
  handler: (event: KeyboardEvent) => void;
}

/**
 * Register global shortcuts.
 *
 * `hotkeys` is read through a ref-free closure that re-registers whenever the
 * array identity changes, so callers should memoise it — or accept one
 * add/remove per render, which is cheap for a single document listener.
 */
export function useHotkeys(hotkeys: Hotkey[], enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    function onKeyDown(event: KeyboardEvent) {
      const typing = isTypingTarget(event.target);

      for (const hotkey of hotkeys) {
        if (event.key.toLowerCase() !== hotkey.key.toLowerCase()) continue;
        if (Boolean(hotkey.meta) !== (event.metaKey || event.ctrlKey)) continue;
        if (hotkey.shift !== undefined && hotkey.shift !== event.shiftKey) continue;
        if (typing && !hotkey.allowInInput) continue;

        event.preventDefault();
        hotkey.handler(event);
        return;
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [hotkeys, enabled]);
}
